/* Isolated draft supplier photo payload implementation. */
(() => {
  'use strict';
  const mount = async root => {
    if (root.dataset.mounted) return;
    root.dataset.mounted = 'true';
    const form = root.closest('form');
    const wrapper = root.closest('[data-product-form]');
    if (!form || !wrapper || !window.CJPhotoCrop) return;
    const query = key => root.querySelector(`[data-cj-photo-${key}]`);
    const file = query('file'), editor = query('editor'), canvas = query('preview');
    const cropInput = query('crop'), engraving = query('engraving'), status = query('status');
    const zoom = query('zoom'), x = query('x'), y = query('y'), zones = query('zone');
    const rotate = query('rotate'), reset = query('reset');
    let product, variant, zone, photo, sourceUrl, overlay;
    let available = root.dataset.available === 'true', busy = false, contractReady = false, previewReady = false;
    let renderSerial = 0, fileSerial = 0;
    const values = new Map();
    const buttons = () => form.querySelectorAll('[data-add-to-cart-trigger]');
    const lock = () => buttons().forEach(button => { button.disabled = busy || !contractReady || !available; });
    const crop = () => window.CJPhotoCrop.cropRect(photo.naturalWidth, photo.naturalHeight, zone.crop_aspect_ratio, Number(zoom.value), Number(x.value), Number(y.value), Number(rotate.value));
    const resolve = asset => asset.kind === 'https' ? asset.reference : root.dataset.assetBase + encodeURIComponent(asset.reference);
    const loadImage = url => new Promise((resolveImage, reject) => {
      const image = new Image();
      image.onload = () => resolveImage(image);
      image.onerror = () => reject(new Error('The preview image could not load.'));
      image.src = url;
    });
    const draw = async () => {
      const serial = ++renderSerial;
      previewReady = false;
      cropInput.disabled = true;
      if (!photo || !zone) return;
      status.textContent = 'Preparing your photo preview.';
      try {
        const currentZone = zone;
        const currentPhoto = photo;
        const rectangle = crop();
        const image = await loadImage(resolve(currentZone.assets.image));
        if (serial !== renderSerial || currentPhoto !== photo || currentZone !== zone) return;
        overlay = image;
        const definition = zone.definition;
        canvas.width = Number(definition.width);
        canvas.height = Number(definition.height);
        const context = canvas.getContext('2d');
        context.clearRect(0, 0, canvas.width, canvas.height);
        // Provider preview uses the crop at these offsets, then its full overlay.
        const cropped = document.createElement('canvas');
        // Bound preview memory while retaining natural coordinates in UploadData.
        const scale = Math.min(1, 2048 / Math.max(rectangle.width, rectangle.height));
        cropped.width = Math.max(1, Math.round(rectangle.width * scale));
        cropped.height = Math.max(1, Math.round(rectangle.height * scale));
        const cropContext = cropped.getContext('2d');
        cropContext.imageSmoothingEnabled = false;
        const bounds = window.CJPhotoCrop.rotatedSize(photo.naturalWidth, photo.naturalHeight, rectangle.rotate);
        cropContext.save();
        cropContext.scale(cropped.width / rectangle.width, cropped.height / rectangle.height);
        cropContext.translate(bounds.width / 2 - rectangle.x, bounds.height / 2 - rectangle.y);
        cropContext.rotate(rectangle.rotate * Math.PI / 180);
        cropContext.drawImage(photo, -photo.naturalWidth / 2, -photo.naturalHeight / 2);
        cropContext.restore();
        if (variant.grayscale) {
          const pixels = cropContext.getImageData(0, 0, cropped.width, cropped.height);
          for (let index = 0; index < pixels.data.length; index += 4) {
            const gray = pixels.data[index] * .3 + pixels.data[index + 1] * .59 + pixels.data[index + 2] * .11;
            pixels.data[index] = pixels.data[index + 1] = pixels.data[index + 2] = gray;
          }
          cropContext.putImageData(pixels, 0, 0);
        }
        context.drawImage(cropped, Number(definition.zone_offset_x), Number(definition.zone_offset_y), Number(definition.zone_width), Number(definition.zone_height));
        context.drawImage(overlay, 0, 0, canvas.width, canvas.height);
        cropInput.value = window.CJPhotoCrop.serialize(rectangle);
        cropInput.disabled = false;
        previewReady = true;
        editor.hidden = false;
        status.textContent = 'Check your photo and engraving carefully before adding to your bag.';
      } catch (error) { status.textContent = error.message || 'Your photo preview could not load. Please try again.'; }
    };
    const renderVariant = id => {
      contractReady = false;
      lock();
      engraving.querySelectorAll('input').forEach(input => values.set(input.name, input.value));
      engraving.replaceChildren();
      variant = product?.variants.find(item => item.id.split('/').pop() === String(id));
      ++renderSerial;
      previewReady = false;
      cropInput.disabled = true;
      if (!variant || !variant.zones.length) {
        status.textContent = 'Photo personalization is not ready for this option.';
        return;
      }
      zone = variant.zones.find(item => item.id === variant.default_zone_id);
      if (!zone) return;
      zones.replaceChildren();
      variant.zones.forEach((item, index) => {
        const option = document.createElement('option');
        option.value = item.id;
        option.textContent = `View ${index + 1}`;
        option.selected = item.id === zone.id;
        zones.append(option);
      });
      variant.fields.forEach((definition, index) => {
        const label = document.createElement('label');
        const input = document.createElement('input');
        input.type = 'text';
        input.id = `cj-photo-engraving-${root.dataset.productId}-${index}`;
        input.name = `properties[${definition.name}]`;
        input.value = values.get(input.name) || '';
        input.required = definition.required === 1;
        input.placeholder = definition.placeholder || '';
        if (definition.maxlength != null) input.maxLength = Number(definition.maxlength);
        if (definition.minlength != null) input.minLength = Number(definition.minlength);
        if (definition.pattern) input.pattern = definition.pattern;
        label.htmlFor = input.id;
        label.textContent = definition.label || definition.name;
        engraving.append(label, input);
      });
      contractReady = true;
      lock();
      if (photo) draw();
      else status.textContent = 'Choose your photo to begin.';
    };
    file.addEventListener('change', async () => {
      const serial = ++fileSerial;
      ++renderSerial;
      previewReady = false;
      cropInput.disabled = true;
      editor.hidden = true;
      photo = null;
      file.setCustomValidity('');
      const chosen = file.files?.[0];
      if (!chosen) return;
      if (!['image/jpeg', 'image/png', 'image/webp'].includes(chosen.type)) {
        file.setCustomValidity('Choose a JPG, PNG or WebP image.');
        file.reportValidity();
        return;
      }
      try {
        if (sourceUrl) URL.revokeObjectURL(sourceUrl);
        sourceUrl = URL.createObjectURL(chosen);
        const decoded = await loadImage(sourceUrl);
        if (serial !== fileSerial) return;
        if (decoded.naturalWidth < 500 || decoded.naturalHeight < 500) throw new Error('Choose a photo at least 500 pixels wide and tall.');
        photo = decoded;
        zoom.value = '1'; x.value = y.value = '.5'; rotate.value = '0';
        await draw();
      } catch (error) {
        file.setCustomValidity(error.message || 'This photo could not open. Choose another image.');
        file.reportValidity();
        status.textContent = file.validationMessage;
      }
    });
    [zoom, x, y, rotate].forEach(input => input.addEventListener('input', draw));
    reset.addEventListener('click', () => { zoom.value = '1'; x.value = y.value = '.5'; rotate.value = '0'; draw(); });
    zones.addEventListener('change', () => { zone = variant.zones.find(item => item.id === zones.value); draw(); });
    const submit = async event => {
      event.preventDefault();
      event.stopImmediatePropagation();
      if (busy || !contractReady || !available) return;
      if (!form.checkValidity()) { form.reportValidity(); return; }
      if (!previewReady || cropInput.disabled) { status.textContent = 'Wait for your photo preview before adding to your bag.'; return; }
      busy = true;
      lock();
      status.textContent = 'Adding your personalized gift to your bag.';
      const controls = Array.from(wrapper.querySelectorAll('select,input,button')).filter(control => !control.disabled);
      const payload = new FormData(form);
      controls.forEach(control => { control.disabled = true; });
      try {
        const action = new URL(form.action, location.href);
        action.pathname = action.pathname.replace(/\/cart\/add\/?$/, '/cart/add.js');
        const response = await fetch(action.href, {method: 'POST', body: payload});
        const result = await response.json();
        if (!response.ok) throw new Error(result.description || result.message || 'Your gift could not be added. Please try again.');
        // A single successful add navigates to the bag; it never creates an order.
        location.assign(action.pathname.replace(/\/add\.js$/, ''));
      } catch (error) {
        controls.forEach(control => { control.disabled = false; });
        busy = false;
        lock();
        status.textContent = error.message || 'Your gift could not be added. Please try again.';
      }
    };
    form.addEventListener('submit', submit, true);
    form.addEventListener('click', event => { if (event.target.closest('[data-add-to-cart-trigger]')) submit(event); }, true);
    wrapper.addEventListener('kit:variant-picker:change', () => { contractReady = false; previewReady = false; cropInput.disabled = true; lock(); });
    wrapper.addEventListener('variant-change', event => {
      available = event.detail?.variant?.available === true;
      renderVariant(event.detail?.variant?.id);
      queueMicrotask(lock);
    });
    lock();
    try {
      const response = await fetch(root.dataset.catalogUrl, {credentials: 'omit'});
      if (!response.ok) throw new Error('Photo options could not load. Please reload this page.');
      const catalog = await response.json();
      product = catalog.products.find(item => item.id.split('/').pop() === root.dataset.productId);
      renderVariant(form.querySelector('[name="id"]')?.value || root.dataset.initialVariant);
    } catch (error) { status.textContent = error.message; }
  };
  document.querySelectorAll('[data-cj-photo]').forEach(mount);
})();
